<template>
  <div>
    <!-- Existing requests first: status is what a returning coach came for -->
    <div v-if="requests.length > 0" class="ibox">
      <div class="ibox-content p-t-md">
        <h2 class="font-heavy m-t-none m-b-none">Your gear requests</h2>
        <div
          v-for="(request, index) in requests"
          :key="request.id"
          :class="index === 0 ? 'p-t-m' : 'p-t-m m-t-m border-t border-solid border-gray-200'"
        >
          <div class="flex flex-wrap align-items-center justify-space-between gap-sm">
            <div>
              <div class="font-semi text-16">{{ request.package_summary }}</div>
              <div class="text-gray-500 text-14">
                Submitted {{ formatDate(request.created_at) }}<template v-if="request.requested_by"> by {{ request.requested_by }}</template>
              </div>
            </div>
            <span v-if="request.status === 'declined'" class="badge-red">Declined</span>
            <span v-else-if="request.status === 'closed'" class="badge-gray">Store closed</span>
          </div>
          <ol v-if="!['declined', 'closed'].includes(request.status)" class="list-unstyled grid grid-cols-5 sm-grid-cols-1 gap-xs m-t-sm m-b-none">
            <li
              v-for="(step, stepIndex) in statusSteps"
              :key="step.value"
              :class="['flex align-items-center gap-x-xxs p-xs rounded-xs text-14', stepClass(request, stepIndex)]"
            >
              <i v-if="stepIndex < stepIndexOf(request)" class="fa-solid fa-check" aria-hidden="true"></i>
              {{ step.label }}
            </li>
          </ol>
          <p class="text-gray-600 text-14 m-t-sm m-b-none">
            {{ statusHelp(request) }}
            Questions? Email <a :href="gearMailto(`Gear request #${request.id}`)" class="text-underline">{{ gearEmail }}</a>.
          </p>
        </div>
        <div v-if="hasActiveRequest" class="border-t border-solid border-gray-200 m-t-md p-t-md">
          <h3 class="font-heavy text-18 m-t-none">What happens next</h3>
          <ol class="gear-steps list-unstyled grid grid-cols-3 md-grid-cols-1 gap-md m-b-none m-t-m">
          <li v-for="(step, stepIndex) in nextSteps" :key="step.title" class="flex align-items-baseline gap-x-sm">
            <span class="gear-step-number">{{ stepIndex + 1 }}</span>
            <div>
              <div class="font-semi text-16">{{ step.title }}</div>
              <p class="text-gray-600 m-t-xxs m-b-none">{{ step.body }}</p>
            </div>
          </li>
        </ol>
        </div>
      </div>
    </div>

    <!-- Hero, stacked: pitch and actions, benefits in one row on white, a thin
         not-a-fit strip, then a footnote -->
    <div class="ibox">
      <div class="ibox-content p-t-md">
        <span class="badge-green">New</span>
        <h1 class="font-heavy m-t-sm m-b-xs">Custom team gear, store included</h1>
        <p class="text-16 text-gray-600 m-b-none">Send us your logo. We run the store and ship it all to your club.</p>
        <div class="flex flex-wrap align-items-center gap-sm m-t-md">
          <button v-if="!showForm" type="button" class="btn btn-primary" data-cy="gear-start" @click="startRequest()">
            Start your team store
          </button>
          <a href="#gear-packages" class="font-semi">Browse the gear <i class="fa-solid fa-angle-down" aria-hidden="true"></i></a>
        </div>

        <dl class="gear-benefits m-t-lg m-b-none">
          <div v-for="(benefit, index) in benefits" :key="benefit.title" class="gear-benefit" :style="{ '--gear-i': index }">
            <span class="gear-benefit-icon"><i :class="benefit.icon" aria-hidden="true"></i></span>
            <dt class="font-semi text-gray-900">{{ benefit.title }}</dt>
            <dd class="text-12 text-gray-500">{{ benefit.detail }}</dd>
          </div>
        </dl>

        <div class="gear-misfits m-t-lg">
          <h2 class="gear-misfits-heading text-red-700">Not a fit for</h2>
          <dl class="gear-misfit-list m-b-none">
            <div v-for="(misfit, index) in misfits" :key="misfit.title" class="gear-misfit" :style="{ '--gear-i': benefits.length + index }">
              <dt class="font-semi text-gray-800"><i class="fa-solid fa-fw fa-xmark text-red-500" aria-hidden="true"></i> {{ misfit.title }}</dt>
              <dd class="text-12 text-gray-500">{{ misfit.detail }}</dd>
            </div>
          </dl>
        </div>
        <p class="text-12 text-gray-500 m-t-sm m-b-none">
          Selling gear you already have?
          <a href="https://help.wrestlingiq.com/article/219-creating-an-online-gear-store" target="_blank" rel="noopener" class="text-underline">Use your own online store instead</a>
          · Questions? <a :href="gearMailto('WIQ Gear question')" class="text-underline">{{ gearEmail }}</a>
        </p>
      </div>
    </div>

    <!-- Intake form -->
    <!-- v-show, not v-if: keeps FilePond and its uploads alive across cancel/reopen -->
    <div v-show="showForm" ref="form" class="ibox">
      <div class="ibox-content p-t-md">
        <div class="gear-form" data-cy="gear-form">
          <div class="flex align-items-start justify-space-between gap-x-sm">
            <div>
              <h2 class="font-heavy m-t-none m-b-none">Start your team store</h2>
              <p class="text-gray-600 m-t-xs m-b-none">
                About 3 minutes. We'll email mockups from {{ gearEmail }} for your approval before anything goes on sale.
              </p>
            </div>
            <button type="button" class="btn btn-default btn-compact" @click="showForm = false">Cancel</button>
          </div>

          <!-- 1. Gear: one partner's blocks, then what depends on them -->
          <section class="gear-form-section">
            <span class="gear-step-number">1</span>
            <div class="min-w-0">
              <h3 id="gear_blocks_label" class="font-heavy m-t-none m-b-xxs">Pick your gear</h3>
              <p class="text-gray-600 m-b-sm">Mix any blocks. Families buy each piece on its own or in a bundle.</p>

              <div v-if="multiplePartners" class="flex flex-wrap align-items-center gap-x-sm m-b-sm">
                <span id="gear_partner_label" class="text-gray-600">Gear partner</span>
                <div class="segmented-control btn-group btn-group-sm" role="group" aria-labelledby="gear_partner_label">
                  <button
                    v-for="partner in catalog.partners"
                    :key="partner.key"
                    type="button"
                    :data-cy="`gear-partner-${partner.key}`"
                    :class="['btn btn-default', { active: formPartner.key === partner.key }]"
                    :aria-pressed="formPartner.key === partner.key ? 'true' : 'false'"
                    @click="choosePartner(partner)"
                  >
                    {{ partner.vendor }}
                  </button>
                </div>
                <span class="text-12 text-gray-500">One partner per store.</span>
              </div>

              <div class="grid grid-cols-3 md-grid-cols-1 gap-sm" role="group" aria-labelledby="gear_blocks_label">
                <label
                  v-for="family in formFamilies"
                  :key="family.key"
                  :for="`gear_block_${family.key}`"
                  :data-cy="`gear-block-${family.type}`"
                  :class="['border border-solid rounded-sm p-sm font-normal m-b-none cursor-pointer', inStore(family) ? 'border-green-500 bg-green-50' : 'border-gray-200']"
                >
                  <input :id="`gear_block_${family.key}`" :name="`gear_block_${family.key}`" :checked="inStore(family)" type="checkbox" class="m-r-xs" @change="toggleBlock(family)">
                  <span class="font-semi">{{ family.name }}</span>
                  <div class="text-gray-500 text-12 m-t-xxs">{{ blockSummary(family) }}</div>
                </label>
              </div>

              <!-- Options on the picked blocks: uniform style, headgear, knee pad -->
              <div v-if="showUniformStyle || formAddOns.length > 0" class="gear-form-options m-t-sm">
                <div v-if="showUniformStyle" class="flex flex-wrap align-items-center gap-x-sm">
                  <span id="gear_uniform_style_label" class="font-semi">Uniform style</span>
                  <div class="segmented-control btn-group btn-group-sm" role="group" aria-labelledby="gear_uniform_style_label">
                    <button
                      v-for="pkg in formUniformFamily.packages"
                      :key="pkg.key"
                      type="button"
                      :class="['btn btn-default', { active: selectedPackage(formUniformFamily).key === pkg.key }]"
                      :aria-pressed="selectedPackage(formUniformFamily).key === pkg.key ? 'true' : 'false'"
                      @click="chooseStyle(formUniformFamily, pkg)"
                    >
                      {{ uniformLabel(pkg.uniform_style) }}
                    </button>
                  </div>
                </div>
                <div v-for="item in formAddOns" :key="item.key" class="checkbox m-t-none m-b-none">
                  <label :for="`gear_add_on_${item.key}`">
                    <input :id="`gear_add_on_${item.key}`" v-model="form.addOnKeys" :name="`gear_add_on_${item.key}`" :value="item.key" type="checkbox">
                    <span class="font-semi">Add {{ item.name.toLowerCase() }}</span>
                    <span class="gear-price text-gray-500">{{ itemPrice(item) }}</span>
                  </label>
                  <p v-if="item.options && item.options.length > 0" class="help-block m-t-none m-b-none">Families choose one: {{ item.options.join(', ') }}.</p>
                  <div v-if="item.key === 'headgear' && form.addOnKeys.includes('headgear')" class="m-t-xs">
                    <label for="gear_headgear_notes" class="font-normal text-gray-700 m-b-xxs">What headgear style do you want? <span class="text-gray-500">(optional)</span></label>
                    <input
                      id="gear_headgear_notes"
                      v-model="form.headgearNotes"
                      name="gear_headgear_notes"
                      type="text"
                      class="form-control"
                      placeholder="e.g. Custom Signature in navy and gold, or let families pick"
                    >
                  </div>
                </div>
              </div>

              <!-- Bundle discounts on the picked blocks, then the coach's own take on
                   bundling. Staff make the call (e.g. bundle-only) downstream. -->
              <div v-if="formKits.length > 0" class="gear-form-kits m-t-md">
                <p class="font-semi text-gray-900 m-b-none">Bundle discounts</p>
                <p class="text-gray-600 m-b-xs">If a family buys a bundle, they save {{ formKits[0].discount_percent }}%. Every piece is also sold on its own.</p>
                <ul class="list-unstyled m-b-none">
                  <li v-for="kit in formKits" :key="kit.key">
                    <span class="min-w-0">
                      <span class="font-semi text-gray-900">{{ kit.name }}</span>
                      <span class="text-gray-600">{{ kitPieces(kit) }}</span>
                    </span>
                    <span v-if="kit.price_cents != null" class="gear-price">
                      <span class="text-gray-400 text-strikethrough">{{ money(kit.full_price_cents) }}</span>
                      <span class="font-semi text-gray-900">{{ money(kit.price_cents) }}</span>
                    </span>
                  </li>
                </ul>
              </div>
              <div v-if="form.packageKeys.length > 0" class="form-group m-t-md m-b-none">
                <label for="gear_bundle_notes">Any special requests for how we should bundle or sell your gear? <span class="font-normal text-gray-500">(optional)</span></label>
                <textarea
                  id="gear_bundle_notes"
                  v-model="form.bundleNotes"
                  name="gear_bundle_notes"
                  rows="2"
                  class="form-control"
                ></textarea>
                <p class="help-block m-b-none">
                  For example: every wrestler buys the singlet, tees and shorts together so the team matches. We wouldn't sell the singlet on its own.
                </p>
              </div>
            </div>
          </section>

          <!-- 2. Fundraiser -->
          <section class="gear-form-section">
            <span class="gear-step-number">2</span>
            <div class="min-w-0">
              <h3 class="font-heavy m-t-none m-b-xxs">Fundraise <span class="font-normal text-gray-500">(optional)</span></h3>
              <p class="text-gray-600 m-b-sm">Add a flat amount to every item. 100% goes to your team after the gear is delivered.</p>
              <label for="gear_markup" class="sr-only">Fundraiser amount per item</label>
              <select id="gear_markup" v-model.number="form.teamMarkupCents" name="gear_markup" class="form-control gear-form-select">
                <option v-for="cents in markupOptions" :key="cents" :value="cents">
                  {{ cents === 0 ? 'None: families pay retail' : `${money(cents)} per item` }}
                </option>
              </select>
              <p class="help-block m-b-none">
                <a href="#gear-fundraiser-calculator" class="text-underline" @click="showCalculator = true">Estimate what you'd raise</a>
              </p>
            </div>
          </section>

          <!-- 3. Logo and colors -->
          <section class="gear-form-section">
            <span class="gear-step-number">3</span>
            <div class="min-w-0">
              <h3 class="font-heavy m-t-none m-b-sm">Your logo and colors</h3>
              <div class="form-group">
                <label>Logo files <span class="text-red-600">*</span></label>
                <pintura-photo-uploader
                  :key="uploaderKey"
                  :access-key="accessKey"
                  :endpoint="endpoint"
                  :cloudfront-base-url="cloudfrontBaseUrl"
                  attachable-type="GearStoreRequest"
                  prefix="gear_logos/"
                  pond-id="gear-logo-uploader"
                  :max-files="5"
                  :edit-images="false"
                  accepted-file-types="image/*, application/pdf, application/postscript, application/illustrator, application/eps, image/x-eps"
                  max-file-size="50MB"
                  label-idle="Drop logo files here or <span class='filepond--label-action'>browse</span>"
                  @photos-changed="photosChanged"
                ></pintura-photo-uploader>
                <p class="help-block m-b-none">
                  AI, EPS, SVG or PDF print best. PNG or JPG works too. Up to 5 files, including a brand guide.
                  Trouble uploading? Email <a :href="gearMailto('Logo files')" class="text-underline">{{ gearEmail }}</a>.
                </p>
              </div>
              <div class="form-group">
                <label for="gear_colors">Team colors</label>
                <input id="gear_colors" v-model="form.teamColors" name="gear_colors" type="text" class="form-control" placeholder="e.g. Navy, gold and white">
              </div>
              <div class="form-group m-b-none">
                <label for="gear_brand_guidelines">Brand guidelines <span class="font-normal text-gray-500">(optional)</span></label>
                <textarea
                  id="gear_brand_guidelines"
                  v-model="form.brandGuidelines"
                  name="gear_brand_guidelines"
                  rows="2"
                  class="form-control"
                  placeholder="e.g. Navy PMS 289 C, gold PMS 123 C. Block lettering, no script."
                ></textarea>
                <p class="help-block m-b-none">Pantone (PMS) colors if you have them, plus fonts or anything our designers should match.</p>
              </div>
            </div>
          </section>

          <!-- 4. Contact and shipping. A complete address shows as one block until Change. -->
          <section class="gear-form-section">
            <span class="gear-step-number">4</span>
            <div class="min-w-0">
              <h3 class="font-heavy m-t-none m-b-sm">Contact and shipping</h3>
              <div class="row">
                <div class="col-xs-12 col-sm-6 form-group">
                  <label for="gear_phone">Your phone <span class="text-red-600">*</span></label>
                  <input id="gear_phone" v-model="form.contactPhone" name="gear_phone" type="tel" class="form-control">
                  <p class="help-block m-b-none">Only for questions about your art.</p>
                </div>
                <div class="col-xs-12 col-sm-6 form-group">
                  <label for="gear_needed_by">Need gear by <span class="font-normal text-gray-500">(optional)</span></label>
                  <input id="gear_needed_by" v-model="form.neededBy" name="gear_needed_by" type="date" class="form-control">
                </div>
              </div>

              <template v-if="editAddress">
                <div class="form-group">
                  <label for="gear_ship_name">Name or attention line <span class="font-normal text-gray-500">(optional)</span></label>
                  <input id="gear_ship_name" v-model="form.shipToName" name="gear_ship_name" type="text" class="form-control">
                </div>
                <div class="form-group">
                  <label for="gear_ship_line1">Address line 1</label>
                  <input id="gear_ship_line1" v-model="form.shipToLine1" name="gear_ship_line1" type="text" class="form-control">
                </div>
                <div class="form-group">
                  <label for="gear_ship_line2">Address line 2 <span class="font-normal text-gray-500">(optional)</span></label>
                  <input id="gear_ship_line2" v-model="form.shipToLine2" name="gear_ship_line2" type="text" class="form-control">
                </div>
                <div class="row">
                  <div class="col-xs-12 col-sm-6 form-group">
                    <label for="gear_ship_city">City</label>
                    <input id="gear_ship_city" v-model="form.shipToCity" name="gear_ship_city" type="text" class="form-control">
                  </div>
                  <div class="col-xs-6 col-sm-2 form-group">
                    <label for="gear_ship_state">State</label>
                    <input id="gear_ship_state" v-model="form.shipToState" name="gear_ship_state" type="text" maxlength="2" class="form-control">
                  </div>
                  <div class="col-xs-6 col-sm-4 form-group">
                    <label for="gear_ship_zip">Zip</label>
                    <input id="gear_ship_zip" v-model="form.shipToZip" name="gear_ship_zip" type="text" class="form-control">
                  </div>
                </div>
              </template>
              <div v-else class="gear-form-address">
                <div>
                  <div class="font-semi text-gray-900">Ships to</div>
                  <div v-if="form.shipToName">{{ form.shipToName }}</div>
                  <div>{{ form.shipToLine1 }}<template v-if="form.shipToLine2">, {{ form.shipToLine2 }}</template></div>
                  <div>{{ form.shipToCity }}, {{ form.shipToState }} {{ form.shipToZip }}</div>
                </div>
                <button type="button" class="btn btn-link btn-compact font-semi" @click="editAddress = true">Change</button>
              </div>
              <p class="help-block">Everything ships in one box to this address, and you hand it out to your families.</p>

            </div>
          </section>

          <!-- 5. Anything else -->
          <section class="gear-form-section">
            <span class="gear-step-number">5</span>
            <div class="min-w-0">
              <h3 class="font-heavy m-t-none m-b-xxs">Anything else? <span class="font-normal text-gray-500">(optional)</span></h3>
              <label for="gear_notes" class="font-normal text-gray-600 m-b-sm">Design ideas, which logo to use where, deadlines, or anything our team should know.</label>
              <textarea id="gear_notes" v-model="form.notes" name="gear_notes" rows="3" class="form-control"></textarea>
            </div>
          </section>

          <div v-if="form.packageKeys.length > 0" class="gear-form-summary">
            <div class="font-semi text-gray-900">Your request</div>
            <div class="text-gray-600">
              {{ pickedVendor }} {{ pickedSummary }}<template v-if="formKits.length > 0"> · {{ formKits.map((kit) => kit.name).join(', ') }}</template>
              · {{ form.teamMarkupCents > 0 ? `${money(form.teamMarkupCents)} per item to your team` : 'No fundraiser' }}
            </div>
          </div>
          <p v-if="error" class="text-danger m-t-sm">{{ errorMessage }}</p>
          <div class="flex flex-wrap align-items-center gap-sm m-t-md">
            <ladda-button :loading="submitting" class="btn btn-primary btn-block-xs-only" @lbClicked="submit">
              Submit request
            </ladda-button>
            <span class="text-gray-500 text-14">Custom gear is final sale, including headgear. No returns or exchanges.</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Packages: one row per lineup; the full item list (with prices) is one click away -->
    <!-- Packages as a pseudo storefront: one section per package type with a
         shared preview photo beside a row per partner. Rows expand in place to
         list their items. Vendor photos show sample designs. -->
    <div id="gear-packages" class="ibox">
      <div class="ibox-content p-t-md">
        <h2 class="font-heavy m-t-none">Build your store</h2>
        <p class="text-gray-600 m-b-none">Add any of these to one store. Retail prices per item; photos show sample designs, and yours uses your logo and colors.</p>

        <section v-for="section in packageSections" :key="section.key" class="gear-type">
          <div class="flex flex-wrap align-items-center gap-x-sm">
            <h3 class="font-heavy text-18 m-t-none m-b-none">{{ section.name }}</h3>
            <span :class="section.audience === 'spirit' ? 'badge-blue' : 'badge-green'">
              {{ section.audience === 'spirit' ? 'Parents & fans' : 'Athletes' }}
            </span>
          </div>
          <p class="text-gray-600 m-t-xxs m-b-none">{{ section.tagline }}</p>

          <div class="gear-shop m-t-sm">
            <!-- Shared preview: no partner owns it. Empty until the coach picks a
                 partner or points at an item, then shows that with a caption. -->
            <figure class="gear-preview m-b-none">
              <!-- Same 4:3 box before and after a pick, so nothing jumps. Before a
                   pick it shows a grid of items taken from every partner in turn. -->
              <div class="gear-preview-media">
                <img v-if="previews[section.key]" :src="previews[section.key].image" :alt="previews[section.key].caption">
                <!-- Several partners: crossfade through each one's main photo until the coach picks something -->
                <transition v-else-if="rotatingEntry(section)" name="gear-fade" mode="out-in">
                  <button
                    :key="rotatingEntry(section).family.key"
                    type="button"
                    class="gear-preview-rotating"
                    :aria-label="`Preview ${rotatingEntry(section).family.vendor} ${section.name}`"
                    @click="previewPackage(section, rotatingEntry(section).family)"
                  >
                    <img :src="rotatingEntry(section).image" alt="">
                  </button>
                </transition>
                <div v-else :class="['gear-preview-collage', `count-${collageItems(section).length}`]">
                  <button
                    v-for="entry in collageItems(section)"
                    :key="`${entry.family.key}:${entry.item.key}`"
                    type="button"
                    :aria-label="`Preview ${entry.family.vendor} ${entry.item.name}`"
                    @click="previewItem(section, entry.family, entry.item)"
                  >
                    <img :src="entry.item.image_url" alt="" loading="lazy">
                  </button>
                </div>
              </div>
              <figcaption class="text-12 text-gray-600 m-t-xs">
                <template v-if="previews[section.key]">{{ previews[section.key].caption }}</template>
                <template v-else-if="rotatingEntry(section)">{{ rotatingEntry(section).family.vendor }} · {{ section.name }}</template>
                <template v-else>Tap any item to see it up close</template>
              </figcaption>
            </figure>

            <div class="gear-partners">
              <article
                v-for="family in section.families"
                :key="family.key"
                :class="['gear-partner', { 'in-store': inStore(family), previewed: previews[section.key] && previews[section.key].familyKey === family.key }]"
                @click="previewPackage(section, family)"
                @focusin="previewPackage(section, family)"
                @mouseenter="rotatingEntry(section) && previewPackage(section, family)"
              >
                <div class="flex justify-space-between align-items-baseline gap-x-sm">
                  <h4 class="font-heavy text-16 text-gray-900 m-t-none m-b-none">{{ family.vendor }}</h4>
                  <span class="gear-price text-14 text-gray-600">Items from <span class="font-semi text-gray-900">{{ money(lowestPriceCents(selectedPackage(family))) }}</span></span>
                </div>

                <ul class="gear-thumbs list-unstyled m-t-sm m-b-none" :aria-label="`${selectedPackage(family).items.length} items`">
                  <li
                    v-for="item in selectedPackage(family).items.slice(0, 6)"
                    :key="item.key"
                    :title="item.name"
                    @mouseenter="previewItem(section, family, item)"
                    @click.stop="previewItem(section, family, item)"
                  >
                    <img v-if="item.image_url" :src="item.image_url" :alt="item.name" loading="lazy">
                  </li>
                  <li v-if="selectedPackage(family).items.length > 6" class="gear-thumbs-more">+{{ selectedPackage(family).items.length - 6 }}</li>
                </ul>

                <!-- Bundles (kits in the catalog): pieces families buy together for less -->
                <ul v-if="selectedPackage(family).kits.length > 0" class="gear-kits list-unstyled m-t-sm m-b-none">
                  <li v-for="group in kitGroups(selectedPackage(family).kits)" :key="group[0].key">
                    <span class="min-w-0">
                      <span class="flex align-items-center gap-x-xxs">
                        <span class="badge-green">Bundle</span>
                        <span class="font-semi text-gray-900">{{ kitPieces(group[0]) }}</span>
                      </span>
                      <span class="text-12 text-gray-500">
                        If a family buys these together, they save {{ group[0].discount_percent }}%. Each piece is also sold on its own.
                        <template v-if="group.length > 1">Men's and women's cuts are separate bundles.</template>
                      </span>
                    </span>
                    <span class="flex flex-col align-items-end">
                      <template v-for="kit in group">
                        <span v-if="kit.price_cents != null" :key="kit.key" class="gear-price text-14">
                          <span v-if="group.length > 1" class="text-12 text-gray-500">{{ kit.cut_label }}</span>
                          <span class="text-gray-400 line-through">{{ money(kit.full_price_cents) }}</span>
                          <span class="font-semi text-gray-900">{{ money(kit.price_cents) }}</span>
                        </span>
                      </template>
                    </span>
                  </li>
                </ul>

                <div v-if="family.packages.length > 1" class="segmented-control btn-group btn-group-sm m-t-sm" role="group" :aria-label="`${family.vendor} ${section.name} uniform style`">
                  <button
                    v-for="pkg in family.packages"
                    :key="pkg.key"
                    type="button"
                    :class="['btn btn-default', { active: selectedPackage(family).key === pkg.key }]"
                    :aria-pressed="selectedPackage(family).key === pkg.key ? 'true' : 'false'"
                    @click="chooseStyle(family, pkg)"
                  >
                    {{ uniformLabel(pkg.uniform_style) }}
                  </button>
                </div>

                <div class="flex flex-wrap align-items-center justify-space-between gap-sm m-t-sm">
                  <button
                    type="button"
                    class="btn btn-link gear-card-toggle"
                    :aria-expanded="expandedCards[family.key] ? 'true' : 'false'"
                    :aria-controls="`gear-items-${family.key}`"
                    @click="$set(expandedCards, family.key, !expandedCards[family.key])"
                  >
                    <i :class="['fa-solid fa-fw', expandedCards[family.key] ? 'fa-angle-down' : 'fa-angle-right']" aria-hidden="true"></i>
                    {{ expandedCards[family.key] ? 'Hide items' : seeAllLabel(selectedPackage(family)) }}
                  </button>
                  <!-- The row's main action: solid until added, then a quiet "added" state that removes on click -->
                  <button
                    type="button"
                    :class="['btn', inStore(family) ? 'btn-default gear-added' : 'btn-primary']"
                    :aria-pressed="inStore(family) ? 'true' : 'false'"
                    :title="inStore(family) ? 'Remove from your store' : null"
                    :disabled="otherPartner(family)"
                    @click="toggleBlock(family)"
                  >
                    <i :class="['fa-solid', inStore(family) ? 'fa-check text-green-600' : 'fa-plus']" aria-hidden="true"></i>
                    {{ inStore(family) ? 'In your store' : 'Add to store' }}
                  </button>
                </div>
                <p v-if="otherPartner(family)" class="text-12 text-gray-500 text-right m-t-xs m-b-none">
                  Your store is with {{ pickedVendor }}. Each store uses one gear partner, so remove those blocks to switch.
                </p>

                <ul v-show="expandedCards[family.key]" :id="`gear-items-${family.key}`" class="gear-item-list list-unstyled m-t-sm m-b-none">
                  <template v-for="(item, itemIndex) in selectedPackage(family).items">
                  <li
                    v-if="startsGroup(selectedPackage(family).items, itemIndex)"
                    :key="`${item.key}-group`"
                    class="text-12 font-semi text-gray-500 uppercase tracking-wide p-t-sm p-b-xxs"
                  >
                    Includes the {{ item.group }}
                  </li>
                  <li :key="item.key" class="gear-item" @mouseenter="previewItem(section, family, item)" @click.stop="previewItem(section, family, item)">
                    <span class="gear-item-photo"><img v-if="item.image_url" :src="item.image_url" alt="" loading="lazy"></span>
                    <div class="min-w-0 flex-1">
                      <div class="flex flex-wrap align-items-center gap-x-xxs">
                        <span class="font-semi text-gray-900">{{ item.name }}</span>
                        <span v-if="item.cuts.length > 1" class="badge-gray">Men's &amp; women's</span>
                        <span v-if="item.optional" class="badge-gray">Optional</span>
                      </div>
                      <div v-if="item.options.length > 0" class="text-12 text-gray-600">Choose one: {{ item.options.join(', ') }}</div>
                      <div class="text-12 text-gray-500">
                        {{ item.sizing }}<template v-if="item.min_order"> · Min {{ item.min_order }}</template>
                      </div>
                    </div>
                    <div :class="['gear-price text-right', item.retail_cents == null ? 'text-gray-500' : 'font-semi']">{{ itemPrice(item) }}</div>
                  </li>
                  </template>
                </ul>
              </article>
            </div>
          </div>
        </section>

        <div v-if="form.packageKeys.length > 0 && !showForm" class="flex flex-wrap align-items-center justify-space-between gap-sm border-t border-solid border-gray-100 m-t-lg p-t-md">
          <span class="text-gray-600">
            In your store: <span class="font-semi text-gray-900">{{ pickedSummary }}</span>
            <template v-if="formKits.length > 0"> · Bundles: <span class="font-semi text-gray-900">{{ formKits.map((kit) => kit.name).join(', ') }}</span></template>
          </span>
          <button type="button" class="btn btn-primary" @click="startRequest()">Start your team store</button>
        </div>
      </div>
    </div>

    <!-- How it works: its own card after the packages. Hidden once a request is
         in flight (the request box above shows the same steps as "What happens next"). -->
    <div v-if="!hasActiveRequest" class="ibox">
      <div class="ibox-content p-t-md">
        <h2 class="font-heavy m-t-none">How it works</h2>
        <p class="text-gray-600 m-b-none">Times are rough. We confirm your dates when we send mockups.</p>
        <ol class="gear-timeline list-unstyled m-t-md m-b-none">
          <li v-for="(step, stepIndex) in nextSteps" :key="step.title">
            <span class="gear-step-number">{{ stepIndex + 1 }}</span>
            <div class="font-semi text-gray-900 m-t-xs">{{ step.title }}</div>
            <div class="text-12 text-gray-500">{{ step.when }}</div>
          </li>
        </ol>
      </div>
    </div>

    <!-- Fundraiser calculator: estimated quantities x the flat markup. Uses
         retail + markup only; WIQ's margin never appears here. -->
    <div id="gear-fundraiser-calculator" class="ibox">
      <div class="ibox-content p-t-md">
        <!-- Collapsed by default; the switch is the whole header row -->
        <button
          type="button"
          class="gear-calc-toggle"
          :aria-expanded="showCalculator ? 'true' : 'false'"
          aria-controls="gear-calc-body"
          @click="showCalculator = !showCalculator"
        >
          <span>
            <span class="gear-calc-title">Fundraiser calculator</span>
            <span class="text-gray-600">See what your team could raise.</span>
          </span>
          <span :class="['gear-switch', { on: showCalculator }]" aria-hidden="true"></span>
        </button>
        <div v-show="showCalculator" id="gear-calc-body" class="gear-calc m-t-md">
          <div>
            <div class="flex flex-wrap align-items-center gap-sm">
              <!-- Same picks as the rows above and the form: one list of blocks -->
              <div class="btn-group btn-group-sm segmented-control segmented-control-stack-xs" role="group" aria-label="Blocks in your store">
                <button
                  v-for="family in families"
                  :key="family.key"
                  type="button"
                  :class="['btn btn-default', { active: calcFamilies.includes(family) }]"
                  :aria-pressed="calcFamilies.includes(family) ? 'true' : 'false'"
                  :disabled="otherPartner(family)"
                  @click="toggleBlock(family)"
                >
                  {{ familyLabel(family) }}
                </button>
              </div>
              <div v-if="calcUniformFamily && calcUniformFamily.packages.length > 1" class="segmented-control btn-group btn-group-sm" role="group" aria-label="Uniform style">
                <button
                  v-for="pkg in calcUniformFamily.packages"
                  :key="pkg.key"
                  type="button"
                  :class="['btn btn-default', { active: selectedPackage(calcUniformFamily).key === pkg.key }]"
                  :aria-pressed="selectedPackage(calcUniformFamily).key === pkg.key ? 'true' : 'false'"
                  @click="chooseStyle(calcUniformFamily, pkg)"
                >
                  {{ uniformLabel(pkg.uniform_style) }}
                </button>
              </div>
            </div>
            <p v-if="form.packageKeys.length === 0" class="text-12 text-gray-500 m-t-xs m-b-none">Showing {{ catalog.partners[0].vendor }}'s athlete blocks. Add or remove blocks to match your store.</p>

            <div class="form-group m-t-md">
              <label id="gear_calc_markup_label">Fundraiser amount per item</label>
              <div class="segmented-control btn-group btn-group-sm" role="group" aria-labelledby="gear_calc_markup_label">
                <button
                  v-for="cents in markupOptions"
                  :key="cents"
                  type="button"
                  :class="['btn btn-default', { active: calcMarkupCents === cents }]"
                  :aria-pressed="calcMarkupCents === cents ? 'true' : 'false'"
                  @click="calcMarkupCents = cents"
                >
                  {{ money(cents) }}
                </button>
              </div>
            </div>

            <ul class="list-unstyled m-b-none">
              <template v-for="row in calcRows">
              <li
                v-if="row.heading"
                :key="`${row.heading}-calc-group`"
                class="text-12 font-semi text-gray-500 uppercase tracking-wide p-t-sm p-b-xxs border-t border-solid border-gray-100"
              >
                {{ row.heading }}
              </li>
              <li
                v-else
                :key="`${row.item.key}-calc`"
                class="gear-calc-row"
              >
                <label :for="`gear_calc_${row.item.key}`" class="font-normal m-b-none">
                  <span class="text-gray-900">{{ row.item.name }}</span>
                  <span v-if="row.item.optional" class="badge-gray m-l-xxs">Optional</span>
                  <span class="gear-price text-12 text-gray-500">
                    {{ itemPrice(row.item) }} + {{ money(calcMarkupCents) }}
                  </span>
                </label>
                <input
                  :id="`gear_calc_${row.item.key}`"
                  :name="`gear_calc_${row.item.key}`"
                  :value="calcQuantity(row.item)"
                  type="number"
                  min="0"
                  step="1"
                  inputmode="numeric"
                  class="form-control gear-calc-qty"
                  @input="setCalcQuantity(row.item, $event.target.value)"
                >
              </li>
              </template>
            </ul>
          </div>

          <div class="gear-calc-summary">
            <div class="text-12 font-semi text-gray-600 uppercase tracking-wide">Your team raises</div>
            <div class="gear-calc-raise">{{ money(calcTotals.raiseCents) }}</div>
            <dl class="m-t-sm m-b-none">
              <div class="flex justify-space-between gap-x-sm p-y-xxs">
                <dt class="font-normal text-gray-600">Items sold</dt>
                <dd class="gear-price">{{ calcTotals.quantity }}</dd>
              </div>
              <div class="flex justify-space-between gap-x-sm p-y-xxs">
                <dt class="font-normal text-gray-600">Families spend about</dt>
                <dd class="gear-price">{{ money(Math.round(calcTotals.spendCents / 100) * 100) }}</dd>
              </div>
            </dl>
            <p class="text-12 text-gray-500 m-t-sm m-b-sm">
              <template v-if="calcMarkupCents > 0">
                {{ money(calcMarkupCents) }} from every item goes to your team. We send the funds after your gear is delivered.
              </template>
              <template v-else>
                With no fundraiser amount, families pay retail and your team raises nothing. Add $5 or more per item to fundraise.
              </template>
              Items with a price range are estimated at the middle of the range.
            </p>
            <button type="button" class="btn btn-default btn-block" @click="startFromCalculator">
              Start with these numbers
            </button>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import LaddaButton from '../../shared/ladda_button.vue';
import PinturaPhotoUploader from './pintura_photo_uploader.vue';
import { photoUploadMix } from './mixins/photo_upload_mix.js';

const STATUS_STEPS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'in_design', label: 'In design' },
  { value: 'art_review', label: 'Art review' },
  { value: 'approved', label: 'Approved' },
  { value: 'live', label: 'Store live' },
];

export default {
  name: 'gear-home',
  components: {
    LaddaButton,
    PinturaPhotoUploader,
  },
  mixins: [photoUploadMix],
  props: {
    // GearCatalog.teaser_json: { types: [...], partners: [{ key, vendor, packages }] }
    catalog: {
      type: Object,
      required: true,
    },
    initialRequests: {
      type: Array,
      default() { return []; },
    },
    team: {
      type: Object,
      required: true,
    },
    markupOptions: {
      type: Array,
      required: true,
    },
    gearEmail: {
      type: String,
      required: true,
    },
    // GearStoreRequest.next_steps -- the confirmation email links here for it
    nextSteps: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      requests: this.initialRequests.slice(),
      showForm: false,
      submitting: false,
      error: false,
      errorMessage: '',
      // Keyed by family key (`${partnerKey}:${type}`): the picked uniform style, and open cards
      familySelection: {},
      expandedCards: {},
      // Section key -> { image, caption, familyKey }: what the shared preview shows.
      // Unset until the coach picks something; the box shows a mixed-partner grid until then.
      previews: {},
      // Advances every few seconds to rotate multi-partner previews; never
      // started for people who prefer reduced motion
      rotationTick: 0,
      rotationTimer: null,
      // Fundraiser calculator: flat markup and estimated quantity per item key
      // (unset entries default to 10). Its blocks are the form's picks.
      calcMarkupCents: 1000,
      calcQuantities: {},
      // Bumped after a submit to remount the uploader empty for the next request
      uploaderKey: 0,
      statusSteps: STATUS_STEPS,
      form: {
        // The one list of picked blocks: row buttons, form checkboxes and the
        // calculator all read and write it
        packageKeys: [],
        addOnKeys: [],
        bundleNotes: '',
        headgearNotes: '',
        teamMarkupCents: 0,
        teamColors: '',
        brandGuidelines: '',
        neededBy: '',
        contactPhone: '',
        shipToName: this.team.name,
        shipToLine1: this.team.address_line_1 || '',
        shipToLine2: this.team.address_line_2 || '',
        shipToCity: this.team.city || '',
        shipToState: this.team.state || '',
        shipToZip: this.team.zip || '',
        notes: '',
      },
      benefits: [
        { icon: 'fa-solid fa-cart-shopping', title: 'Store done for you', detail: 'We build and run it' },
        { icon: 'fa-solid fa-truck', title: 'Free shipping', detail: 'Factory to your club' },
        { icon: 'fa-regular fa-square-check', title: 'Simple choices', detail: 'Mix and match five blocks' },
        { icon: 'fa-solid fa-scale-balanced', title: 'Sales tax handled', detail: 'Your club never files' },
        { icon: 'fa-solid fa-heart', title: 'Built-in fundraiser', detail: 'Great for nonprofit teams' },
      ],
      misfits: [
        { title: 'Shipping to each family', detail: 'We only ship in bulk to your club' },
        { title: 'An always-open store', detail: 'Every store has an end date' },
        { title: 'Returns or exchanges', detail: 'Custom gear is final sale' },
        { title: 'A big catalog', detail: 'We keep it to a few simple blocks' },
      ],
      // Calculator starts collapsed so the page leads with the gear, not a form
      showCalculator: false,
      // Form's partner switch before anything is picked; after that, the picked partner wins
      formPartnerKey: this.catalog.partners[0].key,
      // A complete team address shows as one "Ships to" block until the coach hits Change
      editAddress: !(this.team.address_line_1 && this.team.city && this.team.state && this.team.zip),
    };
  },
  computed: {
    multiplePartners() {
      return this.catalog.partners.length > 1;
    },
    // Every partner's packages, tagged with the partner's name, for the intake form
    allPackages() {
      return this.catalog.partners.flatMap((partner) => partner.packages.map((pkg) => ({ ...pkg, vendor: partner.vendor })));
    },
    // One family per partner and package type: the singlet and 2-piece versions
    // of a lineup share a family and show as one card
    families() {
      const families = [];
      this.catalog.partners.forEach((partner) => {
        partner.packages.forEach((pkg) => {
          const key = `${partner.key}:${pkg.family}`;
          let family = families.find((f) => f.key === key);
          if (!family) {
            const type = this.catalog.types.find((t) => t.key === pkg.family) || { name: pkg.name };
            family = {
              key, type: pkg.family, vendor: partner.vendor, name: type.name, packages: [],
            };
            families.push(family);
          }
          family.packages.push({ ...pkg, vendor: partner.vendor });
        });
      });
      return families;
    },
    // Package types in page order, each with its partners' cards
    packageSections() {
      return this.catalog.types
        .map((type) => ({ ...type, families: this.families.filter((family) => family.type === type.key) }))
        .filter((section) => section.families.length > 0);
    },
    // Picked blocks (one version each), in page order
    pickedPackages() {
      return this.allPackages.filter((pkg) => this.form.packageKeys.includes(pkg.key));
    },
    // The partner this store is with, once anything is picked (one per store)
    pickedVendor() {
      return this.pickedPackages.length > 0 ? this.pickedPackages[0].vendor : null;
    },
    pickedSummary() {
      return this.families.filter((family) => this.inStore(family)).map((family) => {
        const pkg = this.selectedPackage(family);
        return pkg.uniform_style ? `${family.name} (${this.uniformLabel(pkg.uniform_style)})` : family.name;
      }).join(', ');
    },
    // Calculator: the picked blocks, or the first partner's athlete blocks until
    // something is picked (never two partners' blocks in one estimate)
    calcFamilies() {
      if (this.form.packageKeys.length > 0) return this.families.filter((family) => this.inStore(family));
      const { vendor } = this.catalog.partners[0];
      return this.families.filter((family) => family.vendor === vendor && ['uniform', 'warmups', 'training'].includes(family.type));
    },
    calcUniformFamily() {
      return this.calcFamilies.find((family) => family.type === 'uniform') || null;
    },
    // A heading per block, then its items; an item in two blocks (the tee) shows once
    calcRows() {
      const seen = new Set();
      const rows = [];
      this.calcFamilies.forEach((family) => {
        const items = this.selectedPackage(family).items.filter((item) => !seen.has(item.key));
        if (items.length === 0) return;
        rows.push({ heading: family.name });
        items.forEach((item) => {
          seen.add(item.key);
          rows.push({ item });
        });
      });
      return rows;
    },
    calcTotals() {
      let quantity = 0;
      let spendCents = 0;
      this.calcRows.filter((row) => row.item).forEach(({ item }) => {
        const count = this.calcQuantity(item);
        quantity += count;
        spendCents += count * (this.typicalPriceCents(item) + this.calcMarkupCents);
      });
      return { quantity, spendCents, raiseCents: quantity * this.calcMarkupCents };
    },
    // A request still on its way to a live store
    hasActiveRequest() {
      return this.requests.some((request) => ['submitted', 'in_design', 'art_review', 'approved'].includes(request.status));
    },
    // The partner whose blocks the form shows: the store's partner once one is picked
    formPartner() {
      if (this.pickedVendor) return this.catalog.partners.find((partner) => partner.vendor === this.pickedVendor);
      return this.catalog.partners.find((partner) => partner.key === this.formPartnerKey) || this.catalog.partners[0];
    },
    formFamilies() {
      return this.families.filter((family) => family.vendor === this.formPartner.vendor);
    },
    showUniformStyle() {
      return Boolean(this.formUniformFamily) && this.formUniformFamily.packages.length > 1;
    },
    addressComplete() {
      const f = this.form;
      return Boolean(f.shipToLine1 && f.shipToCity && f.shipToState && f.shipToZip);
    },
    formUniformFamily() {
      return this.families.find((family) => family.type === 'uniform' && this.inStore(family)) || null;
    },
    // Optional items in the picked blocks (headgear, knee pad) a coach can add
    formAddOns() {
      const seen = new Set();
      return this.pickedPackages.flatMap((pkg) => pkg.items).filter((item) => {
        if (!item.optional || seen.has(item.key)) return false;
        seen.add(item.key);
        return true;
      });
    },
    // The kits this store gets: cross-block kits whose blocks are all picked
    // (resolved server-side per singlet/2-piece), then each picked block's own
    // kits, minus any a cross-block kit covers -- the larger kit wins.
    // Mirrors GearCatalog#kits_for.
    formKits() {
      const picked = this.form.packageKeys;
      if (picked.length === 0) return [];
      const partner = this.catalog.partners.find((p) => p.packages.some((pkg) => picked.includes(pkg.key)));
      const spanning = partner.spanning_kits.filter((kit) => kit.package_keys.every((key) => picked.includes(key)));
      const covered = new Set(spanning.flatMap((kit) => kit.covers));
      const blockKits = this.pickedPackages.flatMap((pkg) => pkg.kits).filter((kit) => !covered.has(kit.key));
      return [...spanning, ...blockKits];
    },
    logoIds() {
      return this.photoAttachments
        .filter((attachment) => attachment.status === 'uploaded' && !attachment._destroy)
        .map((attachment) => attachment.id);
    },
  },
  mounted() {
    const reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reduceMotion && this.packageSections.some((section) => section.families.length > 1)) {
      this.rotationTimer = setInterval(() => { this.rotationTick += 1; }, 4000);
    }
  },
  beforeDestroy() {
    clearInterval(this.rotationTimer);
  },
  methods: {
    // The partner the rotating preview is on, or null when it doesn't rotate
    // (one partner, reduced motion, or the coach already picked something)
    rotatingEntry(section) {
      if (!this.rotationTimer || section.families.length < 2 || this.previews[section.key]) return null;
      const withPhotos = section.families.filter((family) => this.coverImage(this.selectedPackage(family)));
      if (withPhotos.length < 2) return null;
      const family = withPhotos[this.rotationTick % withPhotos.length];
      return { family, image: this.coverImage(this.selectedPackage(family)) };
    },
    money(cents) {
      const dollars = cents / 100;
      const digits = dollars % 1 === 0 ? 0 : 2;
      return dollars.toLocaleString('en-US', {
        style: 'currency', currency: 'USD', minimumFractionDigits: digits, maximumFractionDigits: digits,
      });
    },
    itemPrice(item) {
      if (item.retail_cents == null) return 'Price TBD';
      if (item.retail_max_cents) return `${this.money(item.retail_cents)}–${this.money(item.retail_max_cents)}`;
      return this.money(item.retail_cents);
    },
    // True where an included package's items begin (catalog `includes:`, unused by today's blocks)
    startsGroup(items, index) {
      return Boolean(items[index].group) && (index === 0 || items[index - 1].group !== items[index].group);
    },
    uniformLabel(style) {
      return style === 'two_piece' ? '2-piece uniform' : 'Singlet';
    },
    // Calculator buttons name the partner once there's more than one
    familyLabel(family) {
      return this.multiplePartners ? `${family.vendor} · ${family.name}` : family.name;
    },
    // Point the section's preview at a partner's package (its cover photo)
    previewPackage(section, family) {
      const image = this.coverImage(this.selectedPackage(family));
      if (!image) return;
      this.$set(this.previews, section.key, { image, caption: `${family.vendor} · ${section.name}`, familyKey: family.key });
    },
    previewItem(section, family, item) {
      if (!item.image_url) return;
      this.$set(this.previews, section.key, { image: item.image_url, caption: `${family.vendor} · ${item.name}`, familyKey: family.key });
    },
    // Up to 4 items for the empty preview, taken from each partner in turn so
    // no partner owns the first look
    collageItems(section) {
      const lists = section.families.map((family) => this.selectedPackage(family).items
        .filter((item) => item.image_url)
        .map((item) => ({ family, item })));
      const picked = [];
      for (let i = 0; picked.length < 4 && lists.some((list) => i < list.length); i += 1) {
        lists.forEach((list) => {
          if (picked.length < 4 && list[i]) picked.push(list[i]);
        });
      }
      return picked;
    },
    seeAllLabel(pkg) {
      const count = pkg.items.length;
      return count === 1 ? 'See the item' : `See all ${count} items`;
    },
    // The package's main photo: its first own item that has one
    coverImage(pkg) {
      const item = pkg.items.find((i) => !i.group && i.image_url) || pkg.items.find((i) => i.image_url);
      return item ? item.image_url : null;
    },
    // Men's and women's kits with the same pieces share one row on a block tile
    kitGroups(kits) {
      const groups = new Map();
      kits.forEach((kit) => {
        const pieces = this.kitPieces(kit);
        groups.set(pieces, [...(groups.get(pieces) || []), kit]);
      });
      return [...groups.values()];
    },
    // "Singlet + 2 tees + shorts": reads as one bundle
    kitPieces(kit) {
      return kit.slots.map((slot, index) => {
        const name = index === 0 ? slot.name : slot.name.toLowerCase();
        return slot.quantity > 1 ? `${slot.quantity} × ${name}` : name;
      }).join(' + ');
    },
    // "Items from": the cheapest core piece. Optional add-ons (headgear, knee
    // pads) would understate a uniform, so they only count if that's all there is.
    lowestPriceCents(pkg) {
      const core = pkg.items.filter((item) => !item.optional);
      const prices = (core.length > 0 ? core : pkg.items).map((item) => item.retail_cents).filter((cents) => cents != null);
      return prices.length > 0 ? Math.min(...prices) : 0;
    },
    calcQuantityKey(item) {
      return item.key;
    },
    calcQuantity(item) {
      const value = this.calcQuantities[this.calcQuantityKey(item)];
      return value === undefined ? 10 : value;
    },
    setCalcQuantity(item, raw) {
      const parsed = parseInt(raw, 10);
      this.$set(this.calcQuantities, this.calcQuantityKey(item), Number.isNaN(parsed) || parsed < 0 ? 0 : parsed);
    },
    // Middle of a price range (e.g. youth vs adult, or a range of headgear choices)
    typicalPriceCents(item) {
      if (item.retail_cents == null) return 0;
      if (!item.retail_max_cents) return item.retail_cents;
      return Math.round((item.retail_cents + item.retail_max_cents) / 2);
    },
    startFromCalculator() {
      this.form.teamMarkupCents = this.calcMarkupCents;
      if (this.form.packageKeys.length === 0) {
        this.form.packageKeys = this.calcFamilies.map((family) => this.selectedPackage(family).key);
      }
      this.startRequest();
    },
    // Is any version of this block in the store?
    inStore(family) {
      return family.packages.some((pkg) => this.form.packageKeys.includes(pkg.key));
    },
    // A block from a different partner than the one already in the store
    otherPartner(family) {
      return Boolean(this.pickedVendor) && family.vendor !== this.pickedVendor;
    },
    toggleBlock(family) {
      const keys = family.packages.map((pkg) => pkg.key);
      if (!this.inStore(family) && this.otherPartner(family)) return;
      if (this.inStore(family)) {
        this.form.packageKeys = this.form.packageKeys.filter((key) => !keys.includes(key));
      } else {
        this.form.packageKeys = [...this.form.packageKeys, this.selectedPackage(family).key];
      }
    },
    // A store is one partner, so switching partners starts the picks over
    choosePartner(partner) {
      if (partner.key === this.formPartner.key) return;
      this.formPartnerKey = partner.key;
      this.form.packageKeys = [];
      this.form.addOnKeys = [];
    },
    // Singlet vs 2-piece: if the block is in the store, swap which version is
    chooseStyle(family, pkg) {
      const wasInStore = this.inStore(family);
      this.$set(this.familySelection, family.key, pkg.key);
      if (wasInStore) {
        const keys = family.packages.map((p) => p.key);
        this.form.packageKeys = [...this.form.packageKeys.filter((key) => !keys.includes(key)), pkg.key];
      }
    },
    // "Singlet, Headgear, Knee pad" for the form's block checkboxes
    blockSummary(family) {
      return this.selectedPackage(family).items.map((item) => item.name).join(', ');
    },
    selectedPackage(family) {
      const key = this.familySelection[family.key];
      return family.packages.find((pkg) => pkg.key === key) || family.packages[0];
    },
    stepIndexOf(request) {
      return STATUS_STEPS.findIndex((step) => step.value === request.status);
    },
    stepClass(request, stepIndex) {
      const current = this.stepIndexOf(request);
      if (stepIndex < current) return 'bg-green-50 text-green-800';
      if (stepIndex === current) return 'bg-green-600 text-white font-semi';
      return 'bg-gray-50 text-gray-500';
    },
    statusHelp(request) {
      switch (request.status) {
        case 'submitted':
          return 'We have your request. We\'ll call you within 2 business days if we have any questions.';
        case 'in_design':
          return 'Our design team is turning your logo into gear mockups. We\'ll email them, with your dates, when they\'re ready.';
        case 'art_review':
          return 'Mockups are ready. Check your email for proofs. Nothing goes on sale until you approve.';
        case 'approved':
          return 'Art approved! We are setting up your online store.';
        case 'live':
          return 'Your store is live. Share the link with your families.';
        case 'closed':
          return 'This store has closed.';
        default:
          return '';
      }
    },
    formatDate(iso) {
      return moment(iso).format('MMM D, YYYY');
    },
    gearMailto(subject) {
      return `mailto:${this.gearEmail}?subject=${encodeURIComponent(subject)}`;
    },
    startRequest() {
      this.showForm = true;
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.scrollIntoView({ behavior: 'smooth' });
      });
    },
    validate() {
      const f = this.form;
      if (f.packageKeys.length === 0) return 'Pick at least one block for your store.';
      const partners = new Set(this.pickedPackages.map((pkg) => pkg.vendor));
      if (partners.size > 1) return 'Pick blocks from one gear partner per store.';
      if (this.photoUploadInFlight) return 'Please wait for your logo to finish uploading.';
      if (this.logoIds.length === 0) return 'Please upload at least one logo file.';
      if (!f.contactPhone.trim()) return 'Please add your phone so we can reach you about the art.';
      if (!f.shipToLine1 || !f.shipToCity || !f.shipToState || !f.shipToZip) return 'Please fill in the full shipping address.';
      return null;
    },
    submit() {
      const vm = this;
      if (vm.submitting) return;

      const problem = vm.validate();
      if (problem) {
        // Open the collapsed address so the error points at fields the coach can see
        if (!vm.addressComplete) vm.editAddress = true;
        vm.errorMessage = problem;
        vm.error = true;
        return;
      }

      vm.error = false;
      vm.submitting = true;
      const f = vm.form;
      const params = {
        gear_store_request: {
          package_keys: f.packageKeys,
          add_on_keys: f.addOnKeys.filter((key) => vm.formAddOns.some((item) => item.key === key)),
          bundle_notes: f.bundleNotes,
          headgear_notes: f.addOnKeys.includes('headgear') ? f.headgearNotes : '',
          team_markup_cents: f.teamMarkupCents,
          team_colors: f.teamColors,
          brand_guidelines: f.brandGuidelines,
          needed_by: f.neededBy || null,
          contact_phone: f.contactPhone,
          ship_to_name: f.shipToName,
          ship_to_line1: f.shipToLine1,
          ship_to_line2: f.shipToLine2,
          ship_to_city: f.shipToCity,
          ship_to_state: f.shipToState,
          ship_to_zip: f.shipToZip,
          notes: f.notes,
          logo_ids: vm.logoIds,
        },
      };

      axios.post(vm.$apiService.gearStoreRequestsUrl(), params)
        .then((response) => {
          vm.submitting = false;
          vm.requests.unshift(response.data);
          vm.showForm = false;
          vm.photoAttachments = [];
          vm.uploaderKey += 1;
          vm.form.packageKeys = [];
          vm.form.addOnKeys = [];
          vm.form.bundleNotes = '';
          vm.form.headgearNotes = '';
          vm.form.notes = '';
          vm.$notificationManager.emit('show-toast', 'Request submitted! Check your email for a confirmation.', true);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        })
        .catch(() => {
          vm.submitting = false;
          vm.errorMessage = `Something went wrong. Please try again or email ${vm.gearEmail}.`;
          vm.error = true;
        });
    },
  },
};
</script>
